import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import {
  ADMIN_VIDEO_SCROLL_DEFAULTS,
  getAdminVideoScrollPreview,
} from "utils/adminVideoScrollEngine";
import { createModalDraftSeedTracker, shouldSeedModalDraft } from "utils/modalDraftSeed";

const FLASH_STYLE_OPTIONS = [
  { value: "black-hold", label: "Black hold" },
  { value: "black-flash", label: "Black flash" },
  { value: "white-flash", label: "White flash" },
];

const normalizeDraft = (settings = {}) => ({
  ...ADMIN_VIDEO_SCROLL_DEFAULTS,
  ...settings,
});

const formatDuration = (durationMs) => {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return "0s";
  const seconds = Math.round(durationMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
};

function AdminVideoScrollCaptureModal({
  isOpen,
  settings,
  error,
  isRunning = false,
  onClose,
  onRun,
}) {
  const [draft, setDraft] = useState(normalizeDraft(settings));
  const draftSeedTrackerRef = useRef(createModalDraftSeedTracker());

  useEffect(() => {
    if (!shouldSeedModalDraft(draftSeedTrackerRef.current, { isOpen, seedKey: "admin-video-scroll" })) {
      return;
    }
    setDraft(normalizeDraft(settings));
  }, [isOpen, settings]);

  const preview = useMemo(() => getAdminVideoScrollPreview(draft), [draft]);
  const estimatedCopy = preview.canScroll
    ? `Estimated scroll: ${formatDuration(preview.durationMs)} over ${Math.round(preview.distance).toLocaleString()} px.`
    : "This page does not currently have enough scrollable content.";

  const updateDraft = (field, value) => {
    setDraft((previousDraft) => ({
      ...previousDraft,
      [field]: value,
    }));
  };

  const handleNumberChange = (field) => (event) => {
    updateDraft(field, Number(event.target.value));
  };

  const handleCheckboxChange = (field) => (event) => {
    updateDraft(field, event.target.checked);
  };

  const handleRun = () => {
    onRun(draft);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={isRunning ? undefined : onClose}
      centered
      className="admin-video-scroll-capture-modal"
    >
      <ModalHeader toggle={isRunning ? undefined : onClose}>
        Video Scroll Capture
      </ModalHeader>
      <ModalBody>
        <p style={{ color: "#475569", marginBottom: "18px" }}>
          Configure a clean, linear page scroll for screen recording. Reopen with{" "}
          <strong>Ctrl/Cmd+Shift+Y</strong>, and press <strong>Escape</strong> to stop a scroll.
        </p>

        {error ? (
          <div
            role="alert"
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "12px",
              color: "#991b1b",
              marginBottom: "16px",
              padding: "10px 12px",
            }}
          >
            {error}
          </div>
        ) : null}

        <FormGroup>
          <Label for="admin-video-scroll-speed">Speed</Label>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <Input
              id="admin-video-scroll-speed"
              type="range"
              min="30"
              max="2000"
              step="10"
              value={draft.pixelsPerSecond}
              onChange={handleNumberChange("pixelsPerSecond")}
            />
            <Input
              aria-label="Scroll speed in pixels per second"
              type="number"
              min="30"
              max="2000"
              step="10"
              value={draft.pixelsPerSecond}
              onChange={handleNumberChange("pixelsPerSecond")}
              style={{ maxWidth: "110px" }}
            />
          </div>
          <small style={{ color: "#64748b" }}>
            Pixels per second. Medium is 120 px/s; max is 2000 px/s.
          </small>
        </FormGroup>

        <FormGroup>
          <Label for="admin-video-scroll-flash-style">Start marker</Label>
          <Input
            id="admin-video-scroll-flash-style"
            type="select"
            value={draft.flashStyle}
            onChange={(event) => updateDraft("flashStyle", event.target.value)}
          >
            {FLASH_STYLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        </FormGroup>

        {draft.flashStyle === "black-hold" ? (
          <FormGroup>
            <Label for="admin-video-scroll-flash-duration">Blackout duration</Label>
            <Input
              id="admin-video-scroll-flash-duration"
              type="number"
              min="50"
              max="5000"
              step="50"
              value={draft.flashDurationMs}
              onChange={handleNumberChange("flashDurationMs")}
            />
            <small style={{ color: "#64748b" }}>
              Milliseconds before the scroll starts.
            </small>
          </FormGroup>
        ) : null}

        <FormGroup>
          <Label for="admin-video-scroll-pause-bottom">Pause at bottom</Label>
          <Input
            id="admin-video-scroll-pause-bottom"
            type="number"
            min="0"
            max="10000"
            step="250"
            value={draft.pauseAtBottomMs}
            onChange={handleNumberChange("pauseAtBottomMs")}
          />
          <small style={{ color: "#64748b" }}>
            Optional hold time in milliseconds after reaching the bottom.
          </small>
        </FormGroup>

        <FormGroup check style={{ marginBottom: "10px" }}>
          <Label check>
            <Input
              type="checkbox"
              checked={draft.scrollToTopFirst}
              onChange={handleCheckboxChange("scrollToTopFirst")}
            />{" "}
            Scroll to top before the marker flash
          </Label>
        </FormGroup>

        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              checked={draft.hideMouseWhileScrolling}
              onChange={handleCheckboxChange("hideMouseWhileScrolling")}
            />{" "}
            Hide mouse cursor while scrolling
          </Label>
        </FormGroup>

        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            color: preview.canScroll ? "#334155" : "#9a3412",
            fontWeight: 600,
            marginTop: "18px",
            padding: "10px 12px",
          }}
        >
          {estimatedCopy}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={isRunning}>
          Cancel
        </Button>
        <Button color="info" className="button-hover-fix" onClick={handleRun} disabled={isRunning}>
          {isRunning ? "Running..." : "Run Scroll"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default AdminVideoScrollCaptureModal;
